// Assignment groups (Universal Work Phase 5) — the "Custom group" reach for
// the Access & Assignment page. A group is a hand-picked list of people with
// two roles: assigners can assign work to every member of that group. Groups
// only take effect when an assignment scope is set to Custom group; the
// work-management-service resolves them server-side on every assign.
// People come from the company-wide directory search (not the caller's
// eligible-assignees scope: the admin is composing scope, not using it).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Plus, Trash2, Users, X } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Switch } from '@/features/shared/components/ui/switch';
import { Textarea } from '@/features/shared/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { PeoplePicker, personSubline, type Person } from '@/features/shared/components/PeoplePicker';
import { searchEmployees } from '@/services/workforceService';
import {
  WORK_GROUP_VISIBILITIES,
  createWorkGroup, deleteWorkGroup, getWorkGroup, putWorkGroupMembers, updateWorkGroup,
  type EligiblePage, type WorkGroup, type WorkGroupMember, type WorkGroupRole, type WorkGroupVisibility,
} from '@/services/workService';
import { useWorkGroups } from '@/features/work/api/hooks';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';

type K = (key: string, dflt: string, opts?: Record<string, unknown>) => string;

/** Company-wide directory search shaped as a PeoplePicker page. The directory
 * endpoint is a top-N search (no paging); an empty query yields nothing, so
 * the picker's empty state tells the admin to type a name. */
export const fetchDirectoryPage = async (q: string, _page: number, scopeSummary = ''): Promise<EligiblePage> => {
  const hits = q.trim() ? await searchEmployees(q) : [];
  const items: Person[] = hits.map((h) => ({
    // The search hit's `id` is the employee NUMBER when the directory has one;
    // group membership (like assignment) keys on the employee record uuid.
    employee_id: h.employee_uuid ?? h.id, user_id: h.user_id, display_name: h.name, job_title: h.sublabel || null,
  }));
  return { items, total: items.length, page: 1, page_size: items.length || 20, has_more: false, reason: 'OK', scope_summary: scopeSummary };
};

const trimOrUndefined = (s: string) => (s.trim() ? s.trim() : undefined);

/** "Assigners can also see:" — what a group grants beyond assignment
 * (Workload Intelligence Phase 1). Older backends omit the field (= none). */
const visibilityLabel = (k: K, v: WorkGroupVisibility): string => (
  v === 'work' ? k('group_vis_work', 'Members’ work')
    : v === 'workload' ? k('group_vis_workload', 'Members’ workload')
      : k('group_vis_none', 'Nothing (assignment only)')
);
const VisibilitySelect: React.FC<{
  k: K; id: string; value: WorkGroupVisibility; disabled?: boolean; onChange: (v: WorkGroupVisibility) => void; className?: string;
}> = ({ k, id, value, disabled, onChange, className = '' }) => (
  <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as WorkGroupVisibility)}
    className={`rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-1.5 ${className}`}>
    {WORK_GROUP_VISIBILITIES.map((v) => <option key={v} value={v}>{visibilityLabel(k, v)}</option>)}
  </select>
);

// ---- Create / edit form (shared) ----
const GroupForm: React.FC<{
  k: K;
  initial?: Pick<WorkGroup, 'name' | 'description' | 'active' | 'visibility_level'>;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (v: { name: string; description: string; active: boolean; visibility_level: WorkGroupVisibility }) => void;
}> = ({ k, initial, busy, onCancel, onSubmit }) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [visibility, setVisibility] = useState<WorkGroupVisibility>(initial?.visibility_level ?? 'none');
  const editing = !!initial;
  const idp = editing ? 'edit' : 'new';
  return (
    <form className="space-y-3 p-4 bg-muted/40" data-testid={`group-form-${idp}`}
      onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSubmit({ name: name.trim(), description, active, visibility_level: visibility }); }}>
      <div className="space-y-1.5">
        <Label htmlFor={`group-${idp}-name`}>{k('group_name', 'Group name')}</Label>
        <Input id={`group-${idp}-name`} value={name} disabled={busy} maxLength={120} autoFocus
          onChange={(e) => setName(e.target.value)} placeholder={k('group_name_ph', 'e.g. Dispatch coordinators')} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`group-${idp}-desc`}>{k('group_description', 'Description (optional)')}</Label>
        <Textarea id={`group-${idp}-desc`} rows={2} value={description} disabled={busy}
          onChange={(e) => setDescription(e.target.value)} placeholder={k('group_description_ph', 'What this group is for.')} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`group-${idp}-visibility`}>{k('group_visibility', 'Assigners can also see')}</Label>
        <VisibilitySelect k={k} id={`group-${idp}-visibility`} value={visibility} disabled={busy} onChange={setVisibility} className="w-full" />
        <p className="text-xs text-muted-foreground">{k('group_visibility_help', 'Workload = counts and band only; work = the members’ work items too. Assignment itself is unaffected.')}</p>
      </div>
      {editing && (
        <div className="flex items-center gap-2">
          <Switch id={`group-${idp}-active`} checked={active} disabled={busy} onCheckedChange={setActive} />
          <Label htmlFor={`group-${idp}-active`} className="text-sm font-normal">{k('group_active', 'Active')}</Label>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onCancel}>{k('group_cancel', 'Cancel')}</Button>
        <Button type="submit" size="sm" disabled={busy || !name.trim()}>
          {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          {editing ? k('group_save', 'Save changes') : k('group_create', 'Create group')}
        </Button>
      </div>
    </form>
  );
};

// ---- Members editor ----
export const GroupMembersDialog: React.FC<{
  group: WorkGroup | null;
  onClose: () => void;
  onSaved: (g: WorkGroup, members: WorkGroupMember[]) => void;
  fetchPage?: (q: string, page: number) => Promise<EligiblePage>;
}> = ({ group, onClose, onSaved, fetchPage }) => {
  const { t } = useTranslation('work');
  const k: K = (key, dflt, opts = {}) => t(`policy_${key}`, { defaultValue: dflt, ...opts }) as string;
  const [members, setMembers] = useState<WorkGroupMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const directoryScope = k('group_directory_scope', 'entire company');
  const search = fetchPage ?? ((q: string, page: number) => fetchDirectoryPage(q, page, directoryScope));

  useEffect(() => {
    if (!group) return;
    let alive = true;
    setLoading(true); setLoadError(null); setDirty(false); setMembers([]);
    getWorkGroup(group.id)
      .then((d) => { if (alive) setMembers(d.members); })
      .catch((e) => { if (alive) setLoadError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [group]);

  const add = (p: Person | null) => {
    if (!p || members.some((m) => m.employee_id === p.employee_id)) return;
    setMembers((ms) => [...ms, {
      employee_id: p.employee_id, user_id: p.user_id ?? null, role: 'member',
      display_name: p.display_name, job_title: p.job_title ?? null, department_name: p.department_name ?? null,
    }]);
    setDirty(true);
  };
  const setRole = (id: string, role: WorkGroupRole) => {
    setMembers((ms) => ms.map((m) => (m.employee_id === id ? { ...m, role } : m)));
    setDirty(true);
  };
  const remove = (id: string) => { setMembers((ms) => ms.filter((m) => m.employee_id !== id)); setDirty(true); };

  const save = async () => {
    if (!group) return;
    setSaving(true);
    try {
      const d = await putWorkGroupMembers(group.id, members.map((m) => ({ employee_id: m.employee_id, role: m.role })));
      toast.success(k('group_members_saved', 'Members saved.'));
      onSaved(d.group || group, d.members);
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('group_members_save_failed', 'Could not save the members.')));
    } finally {
      setSaving(false);
    }
  };

  const assigners = members.filter((m) => m.role === 'assigner').length;
  return (
    <Dialog open={!!group} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{k('group_members_title', 'Members of {{name}}', { name: group?.name ?? '' })}</DialogTitle>
          <DialogDescription>{k('group_members_help', 'Assigners in a group can assign work to every member of that group. Groups apply when the assignment scope is set to Custom group.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="group-add-person">{k('group_add_person', 'Add a person')}</Label>
            <PeoplePicker id="group-add-person" value={null} onChange={add} fetchPage={search}
              disabled={loading || saving} exclude={members.map((m) => m.employee_id)}
              emptyHint={k('group_directory_hint', 'Type a name to search the company directory.')}
              placeholder={k('group_search_ph', 'Search the company directory…')} />
          </div>
          {loading && (
            <p className="text-sm text-muted-foreground flex items-center gap-2" role="status">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
            </p>
          )}
          {!loading && !!loadError && (
            <p className="text-sm text-danger-strong" role="alert">
              {getApiErrorMessage(loadError, k('group_members_load_failed', 'Could not load the members.'))}
            </p>
          )}
          {!loading && !loadError && members.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="group-no-members">{k('group_no_members', 'No members yet. Add people above.')}</p>
          )}
          {members.length > 0 && (
            <ul className="divide-y divide-border rounded-[10px] border border-border max-h-72 overflow-y-auto" data-testid="group-members">
              {members.map((m) => {
                const sub = personSubline({ employee_id: m.employee_id, display_name: m.display_name, job_title: m.job_title, department_name: m.department_name });
                return (
                  <li key={m.employee_id} className="flex items-center gap-3 px-3 py-2" data-testid={`group-member-${m.employee_id}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground">{m.display_name}</span>
                      {sub && <span className="block truncate text-[11px] text-muted-foreground">{sub}</span>}
                    </span>
                    <div role="group" aria-label={k('group_role_of', 'Role of {{name}}', { name: m.display_name })}
                      className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                      {(['assigner', 'member'] as WorkGroupRole[]).map((r) => (
                        <button key={r} type="button" aria-pressed={m.role === r} disabled={saving}
                          className={`px-2 py-1 ${m.role === r ? 'bg-orange-500 text-white' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                          onClick={() => setRole(m.employee_id, r)}>
                          {r === 'assigner' ? k('group_role_assigner', 'Assigner') : k('group_role_member', 'Member')}
                        </button>
                      ))}
                    </div>
                    <button type="button" className="text-muted-foreground hover:text-foreground disabled:opacity-50" disabled={saving}
                      aria-label={k('group_remove_person', 'Remove {{name}}', { name: m.display_name })}
                      onClick={() => remove(m.employee_id)}>
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {members.length > 0 && assigners === 0 && (
            <p className="text-xs text-muted-foreground">{k('group_no_assigners_hint', 'No assigners yet — nobody can assign through this group until at least one member is an assigner.')}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" disabled={saving} onClick={onClose}>{k('group_cancel', 'Cancel')}</Button>
          <Button size="sm" disabled={saving || loading || !!loadError || !dirty} onClick={save}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {k('group_members_save', 'Save members')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Section ----
export const AssignmentGroupsSection: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { t } = useTranslation('work');
  const k: K = (key, dflt, opts = {}) => t(`policy_${key}`, { defaultValue: dflt, ...opts }) as string;
  const { groups, loading, error, reload, setGroups } = useWorkGroups();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [membersOf, setMembersOf] = useState<WorkGroup | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const confirm = useConfirm();

  const create = async (v: { name: string; description: string; visibility_level: WorkGroupVisibility }) => {
    setBusyId('new');
    try {
      const g = await createWorkGroup({ name: v.name, description: trimOrUndefined(v.description), visibility_level: v.visibility_level });
      setGroups((gs) => [...gs, { ...g, member_count: g.member_count ?? 0, assigner_count: g.assigner_count ?? 0 }]);
      setCreating(false);
      toast.success(k('group_created', 'Group created.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('group_create_failed', 'Could not create the group.')));
    } finally {
      setBusyId(null);
    }
  };

  const update = async (g: WorkGroup, v: { name: string; description: string; active: boolean; visibility_level: WorkGroupVisibility }) => {
    setBusyId(g.id);
    try {
      const next = await updateWorkGroup(g.id, { name: v.name, description: v.description.trim(), active: v.active, visibility_level: v.visibility_level });
      setGroups((gs) => gs.map((x) => (x.id === g.id ? { ...x, ...next } : x)));
      setEditingId(null);
      toast.success(k('group_updated', 'Group updated.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('group_update_failed', 'Could not update the group.')));
    } finally {
      setBusyId(null);
    }
  };

  /** Inline visibility change on a row — saves immediately, reverts on failure. */
  const setVisibility = async (g: WorkGroup, visibility_level: WorkGroupVisibility) => {
    const prev = g.visibility_level ?? 'none';
    setGroups((gs) => gs.map((x) => (x.id === g.id ? { ...x, visibility_level } : x)));
    setBusyId(g.id);
    try {
      const next = await updateWorkGroup(g.id, { visibility_level });
      setGroups((gs) => gs.map((x) => (x.id === g.id ? { ...x, ...next, visibility_level: next.visibility_level ?? visibility_level } : x)));
      toast.success(k('group_updated', 'Group updated.'));
    } catch (e) {
      setGroups((gs) => gs.map((x) => (x.id === g.id ? { ...x, visibility_level: prev } : x)));
      toast.error(getApiErrorMessage(e, k('group_update_failed', 'Could not update the group.')));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (g: WorkGroup) => {
    if (!(await confirm({ title: k('group_delete_title', 'Delete {{name}}', { name: g.name }), message: k('group_delete_confirm', 'Delete "{{name}}"? Assignments already made through it are kept; nobody can assign through it anymore.', { name: g.name }), danger: true, confirmLabel: k('group_delete_action', 'Delete group') }))) return;
    setBusyId(g.id);
    try {
      await deleteWorkGroup(g.id);
      setGroups((gs) => gs.filter((x) => x.id !== g.id));
      toast.success(k('group_deleted', 'Group deleted.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('group_delete_failed', 'Could not delete the group.')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="rounded-[12px] border border-border" data-testid="assignment-groups">
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="min-w-0">
          <h2 className="text-sm font-medium text-charcoal flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" /> {k('groups_title', 'Assignment groups')}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {k('groups_help', 'Assigners in a group can assign work to every member of that group. Groups apply when the assignment scope is set to Custom group.')}
          </p>
        </div>
        <Button size="sm" variant="outline" disabled={disabled || creating || loading} onClick={() => { setEditingId(null); setCreating(true); }}>
          <Plus className="w-4 h-4 mr-1" /> {k('groups_new', 'New group')}
        </Button>
      </div>

      {creating && (
        <div className="border-t border-border">
          <GroupForm k={k} busy={busyId === 'new'} onCancel={() => setCreating(false)} onSubmit={create} />
        </div>
      )}

      {loading && groups.length === 0 && (
        <p className="border-t border-border px-4 py-3 text-sm text-muted-foreground flex items-center gap-2" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
        </p>
      )}
      {!loading && !!error && (
        <div className="border-t border-border px-4 py-3 text-sm text-muted-foreground flex items-center justify-between gap-3" role="alert">
          <span>{getApiErrorMessage(error, k('groups_load_failed', 'Could not load assignment groups.'))}</span>
          <Button variant="outline" size="sm" onClick={() => reload()}>{k('retry', 'Retry')}</Button>
        </div>
      )}
      {!loading && !error && groups.length === 0 && !creating && (
        <p className="border-t border-border px-4 py-3 text-sm text-muted-foreground" data-testid="groups-empty">
          {k('groups_empty', 'No assignment groups yet. Create one to use the Custom group scope.')}
        </p>
      )}

      {groups.length > 0 && (
        <ul className="border-t border-border divide-y divide-border">
          {groups.map((g) => (
            <li key={g.id} data-testid={`group-row-${g.id}`}>
              {editingId === g.id ? (
                <GroupForm k={k} initial={g} busy={busyId === g.id} onCancel={() => setEditingId(null)} onSubmit={(v) => update(g, v)} />
              ) : (
                <div className="flex items-start justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-charcoal truncate">{g.name}</span>
                      <Badge variant={g.active ? 'success' : 'outline'} className="text-[10px]">
                        {g.active ? k('group_active', 'Active') : k('group_inactive', 'Inactive')}
                      </Badge>
                    </div>
                    {g.description && <p className="text-xs text-muted-foreground mt-0.5">{g.description}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {k('group_counts', '{{members}} members · {{assigners}} assigners', { members: g.member_count, assigners: g.assigner_count })}
                    </p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <Label htmlFor={`group-vis-${g.id}`} className="text-xs font-normal text-muted-foreground">{k('group_visibility', 'Assigners can also see')}</Label>
                      <VisibilitySelect k={k} id={`group-vis-${g.id}`} value={g.visibility_level ?? 'none'}
                        disabled={disabled || busyId === g.id} onChange={(v) => setVisibility(g, v)} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" disabled={disabled || busyId === g.id} onClick={() => setMembersOf(g)}>
                      <Users className="w-4 h-4 mr-1" /> {k('group_members', 'Members')}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={disabled || busyId === g.id} aria-label={k('group_edit', 'Edit {{name}}', { name: g.name })}
                      onClick={() => { setCreating(false); setEditingId(g.id); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" disabled={disabled || busyId === g.id} aria-label={k('group_delete', 'Delete {{name}}', { name: g.name })}
                      onClick={() => remove(g)}>
                      {busyId === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <GroupMembersDialog group={membersOf} onClose={() => setMembersOf(null)}
        onSaved={(g, members) => setGroups((gs) => gs.map((x) => (x.id === g.id
          ? { ...x, ...g, member_count: members.length, assigner_count: members.filter((m) => m.role === 'assigner').length }
          : x)))} />
    </section>
  );
};

export default AssignmentGroupsSection;
